import { useEffect, useRef, useState } from 'react'
import { Chart, registerables } from 'chart.js'
import { useInvestments } from '../hooks/useData'
import { PageHeader, Card, CardTitle, GoldButton, GhostButton, Modal, Input, Select, MetricCard, fmt, Loading, Empty, colors, fonts } from '../components/UI'
import { useTheme } from '../lib/theme'
import { containsSensitiveData, SENSITIVE_DATA_ERROR } from '../lib/sensitiveDataCheck'

Chart.register(...registerables)

const currentMonth = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}` })()

const TYPES = [
  { value: 'isa', label: 'Stocks & Shares ISA' },
  { value: 'cash_isa', label: 'Cash ISA' },
  { value: 'sipp', label: 'SIPP / Pension' },
  { value: 'gia', label: 'General Investment Account' },
  { value: 'workplace', label: 'Workplace Pension' },
  { value: 'lisa', label: 'Lifetime ISA' },
  { value: 'other', label: 'Other' },
]

const TYPE_COLORS = {
  isa: colors.gold,
  cash_isa: colors.green,
  sipp: colors.blue,
  gia: '#7b68ee',
  workplace: '#e8a84b',
  lisa: '#64b5f6',
  other: colors.muted,
}

export default function InvestmentsPage() {
  const { investments, loading, snapshots, upsertInvestment, deleteInvestment, saveSnapshot, totalValue, monthlyContributions } = useInvestments()
  const { themeId } = useTheme()
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [form, setForm] = useState({ name: '', type: 'isa', provider: '', current_value: '', monthly_contribution: '' })

  const chartRef = useRef(null)
  const chartObj = useRef(null)

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })) }

  function openAdd() {
    setEditing(null)
    setForm({ name: '', type: 'isa', provider: '', current_value: '', monthly_contribution: '' })
    setShowModal(true)
  }

  function openEdit(inv) {
    setEditing(inv)
    setForm({ name: inv.name, type: inv.type, provider: inv.provider || '', current_value: String(inv.current_value), monthly_contribution: String(inv.monthly_contribution || '') })
    setShowModal(true)
  }

  async function handleSave() {
    if (containsSensitiveData(form.name) || containsSensitiveData(form.provider)) {
      setSaveError(SENSITIVE_DATA_ERROR); return
    }
    setSaveError(''); setSaving(true)
    await upsertInvestment({ id: editing?.id, name: form.name, type: form.type, provider: form.provider, current_value: parseFloat(form.current_value) || 0, monthly_contribution: parseFloat(form.monthly_contribution) || 0 })
    await saveSnapshot(currentMonth, totalValue)
    setSaving(false)
    setShowModal(false)
  }

  // Growth chart
  useEffect(() => {
    if (!chartRef.current || snapshots.length === 0) return
    if (chartObj.current) chartObj.current.destroy()
    chartObj.current = new Chart(chartRef.current, {
      type: 'line',
      data: {
        labels: snapshots.map(s => s.month),
        datasets: [{
          data: snapshots.map(s => s.total_value),
          borderColor: colors.gold,
          backgroundColor: 'rgba(201,151,43,0.07)',
          fill: true, tension: 0.4,
          pointRadius: snapshots.length > 6 ? 2 : 4,
          pointBackgroundColor: colors.gold, borderWidth: 2,
        }]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: colors.muted, font: { size: 11 } } },
          y: { grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: colors.muted, font: { size: 11 }, callback: v => '£' + Math.round(v/1000) + 'k' } }
        }
      }
    })
    return () => { if (chartObj.current) chartObj.current.destroy() }
  }, [snapshots, themeId])

  // Allocation by type
  const byType = investments.reduce((acc, inv) => {
    acc[inv.type] = (acc[inv.type] || 0) + (inv.current_value || 0)
    return acc
  }, {})

  if (loading) return <Loading />

  return (
    <div style={{ paddingBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', padding: '18px 20px 4px', position: 'relative' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '20px', color: colors.text }}>Investments</div>
          <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>Portfolio value and growth tracking</div>
        </div>
        <div style={{ position: 'absolute', right: '16px' }}><GoldButton small onClick={openAdd}>+ Add investment</GoldButton></div>
      </div>

      <div style={{ padding: '16px' }}>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: '12px', marginBottom: '20px' }}>
          <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderLeft: '4px solid #c9972b', borderRadius: '12px', padding: '14px' }}><div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.07em', color: `rgba(${colors.textRgb},0.82)`, marginBottom: '6px' }}>TOTAL PORTFOLIO</div><div style={{ fontFamily: fonts.heading, fontSize: '22px', fontWeight: 800, color: colors.text, lineHeight: 1 }}>{fmt(totalValue)}</div><div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '4px' }}>Current value</div></div>
          <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderLeft: '4px solid #3db87a', borderRadius: '12px', padding: '14px' }}><div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.07em', color: `rgba(${colors.textRgb},0.82)`, marginBottom: '6px' }}>MONTHLY CONTRIBUTIONS</div><div style={{ fontFamily: fonts.heading, fontSize: '22px', fontWeight: 800, color: colors.text, lineHeight: 1 }}>{fmt(monthlyContributions)}</div><div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '4px' }}>{investments.length} account{investments.length !== 1 ? 's' : ''}</div></div>
        </div>

        {investments.length === 0 ? (
          <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '28px 16px', marginBottom: '14px', textAlign: 'center' }}>
            <div style={{ fontSize: '13px', fontWeight: 500, color: `rgba(${colors.textRgb},0.82)`, marginBottom: '12px' }}>No investments added yet</div>
            <GoldButton small onClick={openAdd}>Add first investment</GoldButton>
          </div>
        ) : (
          <>
            {/* Portfolio breakdown */}
            <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '16px', marginBottom: '14px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text, marginBottom: '12px' }}>PORTFOLIO BREAKDOWN</div>
              {investments.map(inv => {
                const pct = totalValue > 0 ? (inv.current_value / totalValue * 100) : 0
                const color = TYPE_COLORS[inv.type] || colors.gold
                return (
                  <div key={inv.id} style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <BillLogo name={inv.provider || inv.name} category="Investments" size={38} radius={10} />
                        <div>
                        <div style={{ fontSize: '14px', color: colors.text, fontFamily: fonts.body, fontWeight: 500 }}>{inv.name}</div>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, marginTop: '2px' }}>
                          {TYPES.find(t => t.value === inv.type)?.label}
                          {inv.provider && ` · ${inv.provider}`}
                        </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ fontSize: '16px', color: colors.text, fontFamily: fonts.body, fontWeight: 600, marginRight: '8px' }}>{fmt(inv.current_value)}</span>
                        <button onClick={() => openEdit(inv)} aria-label="Edit" style={{ width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', color: colors.muted, cursor: 'pointer' }}>
                          <i className="ti ti-edit" style={{ fontSize: '16px' }} />
                        </button>
                        <button onClick={() => deleteInvestment(inv.id)} aria-label="Delete" style={{ width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', color: 'rgba(224,82,82,0.6)', cursor: 'pointer' }}>
                          <i className="ti ti-trash" style={{ fontSize: '16px' }} />
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ flex: 1, height: '6px', background: 'rgba(255,255,255,0.07)', borderRadius: 0 }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width 0.5s ease' }} />
                      </div>
                      <span style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, width: '40px', textAlign: 'right' }}>{Math.round(pct)}%</span>
                    </div>
                    {inv.monthly_contribution > 0 && (
                      <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, marginTop: '6px' }}>
                        {fmt(inv.monthly_contribution)}/mo contribution
                      </div>
                    )}
                  </div>
                )
              })}

              {/* Type allocation summary */}
              {Object.entries(byType).length > 1 && (
                <div style={{ marginTop: '8px', paddingTop: '12px', borderTop: `1px solid rgba(255,255,255,0.06)` }}>
                  <div style={{ fontSize: '16px', color: colors.gold, letterSpacing: '0.01em', fontFamily: fonts.body, fontWeight: 700, marginBottom: '10px' }}>By Type</div>
                  {Object.entries(byType).map(([type, value]) => (
                    <div key={type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', fontFamily: fonts.body }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: TYPE_COLORS[type] || colors.gold, flexShrink: 0 }} />
                        <span style={{ fontSize: '14px', fontWeight: 500, color: colors.muted }}>{TYPES.find(t => t.value === type)?.label || type}</span>
                      </div>
                      <span style={{ fontSize: '16px', color: colors.text, fontWeight: 500 }}>{fmt(value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Growth chart */}
            <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '16px', marginBottom: '14px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text, marginBottom: '12px' }}>PORTFOLIO GROWTH</div>
              {snapshots.length < 2 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: colors.muted, fontFamily: fonts.body, fontSize: '16px' }}>
                  Update your portfolio value each month to track growth over time
                </div>
              ) : (
                <div style={{ position: 'relative', height: '200px' }}>
                  <canvas ref={chartRef} role="img" aria-label="Investment portfolio growth chart" />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {showModal && (
        <Modal title={editing ? 'Edit investment' : 'Add investment'} onClose={() => setShowModal(false)}>
          <Input label="Account name" value={form.name} onChange={v => setF('name', v)} placeholder="Vanguard S&S ISA" />
          <Select label="Type" value={form.type} onChange={v => setF('type', v)} options={TYPES} />
          <Input label="Provider (optional)" value={form.provider} onChange={v => setF('provider', v)} placeholder="Vanguard, Hargreaves Lansdown..." />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Input label="Current value" type="number" prefix="£" value={form.current_value} onChange={v => setF('current_value', v)} placeholder="0" />
            <Input label="Monthly contribution" type="number" prefix="£" value={form.monthly_contribution} onChange={v => setF('monthly_contribution', v)} placeholder="0" />
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
            {saveError && <div style={{ fontSize: '14px', color: colors.red, fontFamily: fonts.body, marginBottom: '10px', padding: '8px 12px', background: 'rgba(224,82,82,0.1)', borderRadius: '10px' }}>{saveError}</div>}
            <GhostButton onClick={() => setShowModal(false)}>Cancel</GhostButton>
            <GoldButton onClick={handleSave}>{saving ? 'Saving...' : 'Save'}</GoldButton>
          </div>
        </Modal>
      )}
    </div>
  )
}
